import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * CookieBanner — the site-wide cookie notice. This static site sets no
 * tracking cookies, so the banner is informational: it tells the visitor
 * exactly that, links to the /legal/cookies page, and once dismissed writes a
 * single localStorage flag (the very preference the notice describes) so it
 * never shows again.
 *
 * Behaviour:
 *   • appears after a short delay so it never competes with a page's opening
 *     moments, and slides up gently (plain CSS transition, disabled under
 *     `motion-reduce` so reduced-motion visitors get an instant appearance);
 *   • fixed to the bottom corner and never blocks content — the page stays
 *     fully scrollable and interactive behind it;
 *   • fully keyboard-accessible (focus-visible rings on both controls);
 *   • localStorage access is wrapped in try/catch so private-browsing modes
 *     that block storage degrade gracefully (the notice simply reappears).
 *
 * The key name is documented on /legal/cookies — keep the two in sync.
 */

const STORAGE_KEY = 'ibas-cookie-notice';
const DISMISSED = 'dismissed';
const SHOW_DELAY_MS = 1600;

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === DISMISSED) return;
    } catch {
      // Storage unavailable (e.g. blocked in private browsing) — still show.
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Flip the transition class one frame after mounting so the slide-up runs.
  useEffect(() => {
    if (!visible) return;
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, DISMISSED);
    } catch {
      // Storage unavailable — dismiss for this visit only.
    }
    setVisible(false);
  };

  return (
    <section
      aria-label="Cookie notice"
      className={`fixed inset-x-4 bottom-4 z-[60] rounded-2xl border border-white/15 bg-brand-navy p-5 shadow-2xl transition-all duration-500 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm motion-reduce:transition-none ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100'
      }`}
    >
      <p className="text-sm leading-relaxed text-white/80">
        This site sets no tracking cookies. The only thing stored on your device is a small browser
        preference — including one so this notice stays dismissed.
      </p>
      <div className="mt-4 flex items-center gap-4">
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex items-center rounded-full bg-brand-gold px-5 py-2 font-label text-sm font-semibold text-brand-navy transition-all hover:-translate-y-0.5 hover:bg-brand-gold-light focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
        >
          Got it
        </button>
        <Link
          to="/legal/cookies"
          className="text-sm font-semibold text-white/70 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white hover:decoration-brand-gold-light focus-visible:text-white focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none"
        >
          Cookies policy
        </Link>
      </div>
    </section>
  );
}
